import { createContext, useContext, useEffect, useState } from "react";
import { Session, User, Provider } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabaseClient";
import { Shop } from "@/types/Shops";

interface AuthContextType {
    session: Session | null;
    signInWithOtp: (email: string) => Promise<{ data: any; error: any }>;
    signInWithOAuth: (provider: Provider) => Promise<{ data: any; error: any }>;
    signInWithPassword: (
        email: string,
        password: string
    ) => Promise<{ data: any; error: any }>;
    signOut: () => Promise<{ error: any }>;
    refresh: () => Promise<{ data: any; error: any }>;
    isDemoMode: boolean;
    shop: Shop | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [session, setSession] = useState<Session | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [shop, setShop] = useState<Shop | null>(null);

    useEffect(() => {
        const { data: listener } = supabase.auth.onAuthStateChange(
            (_event, session) => {
                setSession(session);
                setUser(session?.user || null);
                setLoading(false);
            }
        );
        return () => {
            listener?.subscription.unsubscribe();
        };
    }, []);

    useEffect(() => {
        const fetchShop = async () => {
            const { data, error } = await supabase
                .from("shops")
                .select("*")
                .single();
            if (error) {
                console.error("Error fetching shop: ", error);
            } else {
                setShop(data);
            }
        };
        fetchShop();
    }, [session]);

    const signInWithOtp = async (email: string) => {
        const { data, error } = await supabase.auth.signInWithOtp({ email });
        return { data, error };
    };

    const signInWithOAuth = async (provider: Provider) => {
        const { data, error } = await supabase.auth.signInWithOAuth({
            provider: provider,
        });
        return { data, error };
    };

    const signInWithPassword = async (email: string, password: string) => {
        const { data, error } = await supabase.auth.signInWithPassword({
            email: email,
            password: password,
        });
        return { data, error };
    };

    const signOut = async () => {
        const { error } = await supabase.auth.signOut();
        return { error };
    };

    const refresh = async () => {
        const { data, error } = await supabase.auth.refreshSession();
        return { data, error };
    };

    // Calculate if we're in demo mode based on the user's email
    const isDemoMode = user?.email === "demo@getoptifi.com";

    return (
        <AuthContext.Provider
            value={{
                session,
                signInWithOtp,
                signInWithOAuth,
                signInWithPassword,
                signOut,
                refresh,
                isDemoMode,
                shop,
            }}
        >
            {!loading && children}
        </AuthContext.Provider>
    );
}

// Custom hook to use the auth context
export function useAuth() {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
}
